import { CargoTypeEntity } from 'Models/Entities';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { getMeasurementValueFromId } from 'Util/_HumanWritten/MeasurementUtils';
import { BookingWizardData } from '../../../BookingWizardData/BaseBookingWizardData';
import {
	isVehicleBookingWizardData,
	VehicleBookingWizardData,
} from '../../../BookingWizardData/VehicleBookingWizardData';
import { store } from 'Models/Store';
import { appConfigStore } from 'Models/AppConfigStore';

const VehicleTabActions = {
	ClearDetails: 'clear_details',
	ChangeMake: 'change_make',
	ChangeModel: 'change_model',
	ChangeWidth: 'change_width',
	HasWidthDetails: 'has_width_details',
	ChangeTrailerWidth: 'change_trailer_width',
	HasTrailerWidthDetails: 'has_trailer_width_details',
	ChangeCharacteristic: 'change_characteristic',
	ChangeTrailerCharacteristic: 'change_trailer_characteristic',
	ToggleInheritedCharacteristic: 'toggle_inherited_characteristic',
} as const;

interface ChangeModelArgs {
	cargoTypes: CargoTypeEntity[];
	oldWizardData: BookingWizardData | null;
}

interface ChangeWidthArgs {
	widthId: string;
}

interface ChangeCharacteristicArgs {
	characteristicId: string;
	name: string;
}

interface HasWidthDetailsArgs {
	checked: boolean;
}

type VehicleTabAction =
	| { type: typeof VehicleTabActions.ClearDetails }
	| { type: typeof VehicleTabActions.ChangeMake }
	| { type: typeof VehicleTabActions.ChangeModel; payload: ChangeModelArgs }
	| { type: typeof VehicleTabActions.ChangeWidth; payload: ChangeWidthArgs }
	| { type: typeof VehicleTabActions.HasWidthDetails; payload: HasWidthDetailsArgs }
	| { type: typeof VehicleTabActions.ChangeTrailerWidth; payload: ChangeWidthArgs }
	| { type: typeof VehicleTabActions.HasTrailerWidthDetails; payload: HasWidthDetailsArgs }
	| { type: typeof VehicleTabActions.ChangeCharacteristic; payload: ChangeCharacteristicArgs }
	| { type: typeof VehicleTabActions.ChangeTrailerCharacteristic; payload: ChangeCharacteristicArgs }
	| { type: typeof VehicleTabActions.ToggleInheritedCharacteristic; payload: ChangeCharacteristicArgs };

export const VehicleTabActionCreators = {
	clearDetails: (): VehicleTabAction => ({ type: VehicleTabActions.ClearDetails }),
	changeMake: (): VehicleTabAction => ({ type: VehicleTabActions.ChangeMake }),
	changeModel: (payload: ChangeModelArgs): VehicleTabAction => ({ type: VehicleTabActions.ChangeModel, payload }),
	changeWidth: (payload: ChangeWidthArgs): VehicleTabAction => ({ type: VehicleTabActions.ChangeWidth, payload }),
	hasWidthDetails: (payload: HasWidthDetailsArgs): VehicleTabAction => ({
		type: VehicleTabActions.HasWidthDetails,
		payload,
	}),
	changeTrailerWidth: (payload: ChangeWidthArgs): VehicleTabAction => ({
		type: VehicleTabActions.ChangeTrailerWidth,
		payload,
	}),
	hasTrailerWidthDetails: (payload: HasWidthDetailsArgs): VehicleTabAction => ({
		type: VehicleTabActions.HasTrailerWidthDetails,
		payload,
	}),
	changeCharacteristic: (payload: ChangeCharacteristicArgs): VehicleTabAction => ({
		type: VehicleTabActions.ChangeCharacteristic,
		payload,
	}),
	changeTrailerCharacteristic: (payload: ChangeCharacteristicArgs): VehicleTabAction => ({
		type: VehicleTabActions.ChangeTrailerCharacteristic,
		payload,
	}),
	toggleInheritedCharacteristic: (payload: ChangeCharacteristicArgs): VehicleTabAction => ({
		type: VehicleTabActions.ToggleInheritedCharacteristic,
		payload,
	}),
};

/**
 * Clears the per-vehicle exact-weight state so it re-prefills from the newly
 * selected vehicle type. Resetting the user-edited flag is essential — the
 * prefill effect in the input skips while it is true.
 */
const resetVehicleExactWeight = (innerWizardData: VehicleBookingWizardData) => {
	innerWizardData.vehicleExactWeight = null;
	innerWizardData.vehicleExactWeightUnit = null;
	innerWizardData.vehicleExactWeightUserEdited = false;
};

export const wizardDataVehicleTabReducer = (wizardData: BookingWizardData, action: VehicleTabAction) => {
	const newData = new BookingWizardData({ ...wizardData });

	if (!isVehicleBookingWizardData(newData.innerWizardData)) {
		return null;
	}

	switch (action.type) {
		case VehicleTabActions.ClearDetails: {
			newData.innerWizardData.cargoMake = '';
			newData.innerWizardData.cargoModel = '';
			newData.innerWizardData.vehicleLengthId = '';
			newData.innerWizardData.vehicleWeightId = '';
			newData.innerWizardData.showWidthDetails = false;
			newData.innerWizardData.vehicleWidthId = '';
			newData.innerWizardData.cargoTypeId = '';
			newData.innerWizardData.removedInheritedCharacteristics = [];
			resetVehicleExactWeight(newData.innerWizardData);
			return newData;
		}
		case VehicleTabActions.ChangeMake: {
			// Clear all except for cargoMake
			newData.innerWizardData.cargoModel = '';
			newData.innerWizardData.vehicleLengthId = '';
			newData.innerWizardData.vehicleWeightId = '';
			newData.innerWizardData.showWidthDetails = false;
			newData.innerWizardData.vehicleWidthId = '';
			newData.innerWizardData.cargoTypeId = '';
			newData.innerWizardData.removedInheritedCharacteristics = [];
			resetVehicleExactWeight(newData.innerWizardData);
			return newData;
		}
		case VehicleTabActions.ChangeModel: {
			return handleChangeModel(wizardData, action);
		}
		case VehicleTabActions.ChangeWidth: {
			const { widthId } = action.payload;
			newData.innerWizardData.vehicleWidthId = widthId;
			return newData;
		}
		case VehicleTabActions.HasWidthDetails: {
			const { checked } = action.payload;
			// Always clear the width when toggling — when checked, the user is required to pick one explicitly.
			newData.innerWizardData.vehicleWidthId = '';
			newData.innerWizardData.showWidthDetails = checked;
			// When the vehicle and trailer widths are linked, keep the trailer checkbox in sync.
			if (appConfigStore.vehicleAndTrailerWidthLinked) {
				newData.innerWizardData.showTrailerWidthDetails = checked;
				newData.innerWizardData.trailerWidthId = '';
			}
			return newData;
		}
		case VehicleTabActions.ChangeTrailerWidth: {
			const { widthId } = action.payload;
			newData.innerWizardData.trailerWidthId = widthId;
			return newData;
		}
		case VehicleTabActions.HasTrailerWidthDetails: {
			const { checked } = action.payload;
			// Clear the trailer width when toggling — when checked the user must pick one explicitly.
			newData.innerWizardData.trailerWidthId = '';
			newData.innerWizardData.showTrailerWidthDetails = checked;
			// When linked, keep the vehicle checkbox in sync (the trailer follows the vehicle width).
			if (appConfigStore.vehicleAndTrailerWidthLinked) {
				newData.innerWizardData.showWidthDetails = checked;
				newData.innerWizardData.vehicleWidthId = '';
			}
			return newData;
		}
		case VehicleTabActions.ChangeCharacteristic: {
			const { characteristicId, name } = action.payload;
			const match = newData.innerWizardData.cargoCharacteristics.find(
				x => x.characteristicId === characteristicId,
			);
			if (match) {
				// Remove
				newData.innerWizardData.cargoCharacteristics = newData.innerWizardData.cargoCharacteristics.filter(
					x => x.characteristicId !== characteristicId,
				);
			} else {
				// Add
				newData.innerWizardData.cargoCharacteristics = [
					...newData.innerWizardData.cargoCharacteristics,
					{ characteristicId, name },
				];
			}
			return newData;
		}
		case VehicleTabActions.ChangeTrailerCharacteristic: {
			const { characteristicId, name } = action.payload;
			const match = newData.innerWizardData.trailerCargoCharacteristics.find(
				x => x.characteristicId === characteristicId,
			);
			if (match) {
				// Remove
				newData.innerWizardData.trailerCargoCharacteristics =
					newData.innerWizardData.trailerCargoCharacteristics.filter(
						x => x.characteristicId !== characteristicId,
					);
			} else {
				// Add
				newData.innerWizardData.trailerCargoCharacteristics = [
					...newData.innerWizardData.trailerCargoCharacteristics,
					{ characteristicId, name },
				];
			}
			return newData;
		}
		case VehicleTabActions.ToggleInheritedCharacteristic: {
			// Unchecking an inherited characteristic records it in removedInheritedCharacteristics;
			// re-checking it removes that record. The inherited characteristic itself stays on the
			// cargo type — this only opts this booking out of it.
			const { characteristicId, name } = action.payload;
			const removed = newData.innerWizardData.removedInheritedCharacteristics ?? [];
			const match = removed.find(x => x.characteristicId === characteristicId);
			if (match) {
				newData.innerWizardData.removedInheritedCharacteristics = removed.filter(
					x => x.characteristicId !== characteristicId,
				);
			} else {
				newData.innerWizardData.removedInheritedCharacteristics = [...removed, { characteristicId, name }];
			}
			return newData;
		}
		default: {
			return newData;
		}
	}
};

const handleChangeModel = (
	wizardData: BookingWizardData<VehicleBookingWizardData>,
	action: { type: typeof VehicleTabActions.ChangeModel; payload: ChangeModelArgs },
) => {
	const newData = new BookingWizardData({ ...wizardData });

	if (!isVehicleBookingWizardData(newData.innerWizardData)) {
		return null;
	}

	const { cargoTypeCharacteristicsMap, cargoCharacteristicMap } = store;
	const { cargoTypes, oldWizardData } = action.payload;
	const { cargoModel, cargoMake } = newData.innerWizardData;
	const selectedVehicleType = cargoTypes.filter(x => x.cargoModel === cargoModel && x.cargoMake === cargoMake)[0];

	if (!!selectedVehicleType) {
		newData.innerWizardData.cargoTypeId = selectedVehicleType.id;
		// The inherited characteristic set belongs to the cargo type, so any per-booking removals
		// no longer apply once the type changes.
		newData.innerWizardData.removedInheritedCharacteristics = [];
		// The exact-weight input is pre-filled per vehicle type, so clear any
		// previously entered value (and the user-edited flag) on a type change
		// — otherwise the prefill effect would skip and carry the old type's
		// weight over to the new one, submitting the wrong band.
		resetVehicleExactWeight(newData.innerWizardData);
		if (wizardData.restrictedMode && isNotNullOrUndefined(oldWizardData)) {
			const oldLengthId = (oldWizardData.innerWizardData as VehicleBookingWizardData).vehicleLengthId;
			const oldWeightId = (oldWizardData.innerWizardData as VehicleBookingWizardData).vehicleWeightId;

			const minLength = getMeasurementValueFromId(selectedVehicleType.minimumLengthId);
			const oldLength = getMeasurementValueFromId(oldLengthId);
			newData.innerWizardData.vehicleLengthId =
				minLength < oldLength ? oldLengthId : selectedVehicleType.minimumLengthId;

			const minWeight = getMeasurementValueFromId(selectedVehicleType.minimumWeightId);
			const oldWeight = getMeasurementValueFromId(oldWeightId);
			newData.innerWizardData.vehicleWeightId =
				minWeight < oldWeight ? oldWeightId : selectedVehicleType.minimumWeightId;

			// TODO: When does this case occur?
		} else {
			newData.innerWizardData.vehicleLengthId = selectedVehicleType.minimumLengthId;
			newData.innerWizardData.vehicleWeightId = selectedVehicleType.minimumWeightId;

			const cargoCharacteristics = cargoTypeCharacteristicsMap[selectedVehicleType.id];
			if (cargoCharacteristics) {
				// Remove selectable characteristics
				newData.innerWizardData.cargoCharacteristics = [];
			}

			if (selectedVehicleType.minimumWidthId) {
				newData.innerWizardData.showWidthDetails = true;
				newData.innerWizardData.vehicleWidthId = selectedVehicleType.minimumWidthId;
			} else {
				newData.innerWizardData.showWidthDetails = false;
				newData.innerWizardData.vehicleWidthId = '';
			}
		}
		newData.disabledVehicleType = false;
	}

	return newData;
};
